import React from 'react';
import { motion } from 'motion/react';
import { MapPin, TrendingUp, Search, X, ArrowRight, Car, AlertTriangle, Fuel, Zap, ShoppingBag, CloudSun, Globe } from 'lucide-react';

interface LocationSearchHeaderProps {
  userLocation?: { state: string; lga: string };
  onLocationClick: (location: string) => void;
  searchQuery?: string;
  onSearch?: (query: string) => void;
  searchHistory?: string[];
  onCategoryChange?: (category: string) => void;
  selectedCategory?: string;
}

export function LocationSearchHeader({ 
  userLocation, 
  onLocationClick, 
  searchQuery, 
  onSearch,
  onCategoryChange,
  selectedCategory = 'all'
}: LocationSearchHeaderProps) {
  const [localSearch, setLocalSearch] = React.useState('');

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (localSearch.trim() && onSearch) {
      onSearch(localSearch.trim());
    }
  };

  const handleClearSearch = () => {
    setLocalSearch('');
    if (onSearch) {
      onSearch('');
    }
  };

  const categories = [
    { id: 'all', label: 'All', icon: Globe, color: 'from-gray-500 to-gray-600' },
    { id: 'traffic', label: 'Traffic', icon: Car, color: 'from-red-500 to-red-600' },
    { id: 'crime', label: 'Crime', icon: AlertTriangle, color: 'from-orange-500 to-orange-600' },
    { id: 'fuel', label: 'Fuel', icon: Fuel, color: 'from-blue-500 to-blue-600' },
    { id: 'power', label: 'Power', icon: Zap, color: 'from-amber-500 to-amber-600' },
    { id: 'market', label: 'Market', icon: ShoppingBag, color: 'from-green-500 to-green-600' },
    { id: 'weather', label: 'Weather', icon: CloudSun, color: 'from-cyan-500 to-cyan-600' }
  ];

  const popularLocations = [
    { name: 'Lagos Island', emoji: '🏝️', state: 'Lagos' },
    { name: 'Victoria Island', emoji: '🌊', state: 'Lagos' },
    { name: 'Lekki', emoji: '🏖️', state: 'Lagos' },
    { name: 'Ikeja', emoji: '✈️', state: 'Lagos' },
    { name: 'Yaba', emoji: '💻', state: 'Lagos' },
    { name: 'Abuja Central', emoji: '🏛️', state: 'FCT' },
    { name: 'Surulere', emoji: '🏘️', state: 'Lagos' },
    { name: 'Ikoyi', emoji: '🌳', state: 'Lagos' }
  ];

  return (
    <motion.div
      initial={{ opacity: 0, y: -20 }}
      animate={{ opacity: 1, y: 0 }}
      className="mb-4 space-y-4"
    >
      {/* Search Input */}
      <form onSubmit={handleSearchSubmit} className="relative">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
        <input
          type="text"
          value={localSearch}
          onChange={(e) => setLocalSearch(e.target.value)}
          placeholder="Search for any location... (e.g., Ikeja, Lekki, Surulere)"
          className="w-full pl-12 pr-24 py-3.5 bg-white border-2 border-gray-200 rounded-2xl focus:outline-none focus:ring-4 focus:ring-green-100 focus:border-green-500 transition-all text-sm font-medium text-gray-800 placeholder:text-gray-400 shadow-sm"
        />
        <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1">
          {localSearch && (
            <>
              <motion.button
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                type="submit"
                className="w-9 h-9 rounded-xl bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 flex items-center justify-center transition-all shadow-md hover:shadow-lg"
              >
                <ArrowRight className="w-5 h-5 text-white" />
              </motion.button>
              <button
                type="button"
                onClick={handleClearSearch}
                className="w-9 h-9 rounded-xl bg-gray-200 hover:bg-gray-300 flex items-center justify-center transition-colors"
              >
                <X className="w-4 h-4 text-gray-600" />
              </button>
            </>
          )}
          {!localSearch && searchQuery && (
            <button
              type="button"
              onClick={handleClearSearch}
              className="w-9 h-9 rounded-xl bg-gray-200 hover:bg-gray-300 flex items-center justify-center transition-colors"
            >
              <X className="w-4 h-4 text-gray-600" />
            </button>
          )}
        </div>
      </form>

      {/* Category Filter Chips */}
      {onCategoryChange && (
        <div className="space-y-2">
          <div className="flex items-center gap-2 px-1">
            <span className="text-xs font-black text-gray-700 uppercase tracking-wider">
              Filter by Type
            </span>
          </div>
          <div className="flex gap-2 overflow-x-auto pb-2 no-scrollbar">
            {categories.map((category) => {
              const isActive = selectedCategory === category.id;
              const Icon = category.icon;
              
              return (
                <motion.button
                  key={category.id}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => onCategoryChange(category.id)}
                  className={`flex-shrink-0 flex items-center gap-2 px-4 py-2.5 rounded-xl font-bold text-sm transition-all border-2 ${
                    isActive
                      ? `bg-gradient-to-r ${category.color} text-white border-transparent shadow-lg`
                      : 'bg-white text-gray-700 border-gray-200 hover:border-gray-300 shadow-sm'
                  }`}
                >
                  <Icon className={`w-4 h-4 ${isActive ? 'text-white' : 'text-gray-500'}`} />
                  <span className="whitespace-nowrap">{category.label}</span>
                  {isActive && (
                    <motion.span
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      className="text-xs"
                    >
                      ✓
                    </motion.span>
                  )}
                </motion.button>
              );
            })}
          </div>
        </div>
      )}

      {/* Popular Locations Header */}
      <div className="flex items-center gap-2 mb-3 px-1">
        <TrendingUp className="w-4 h-4 text-green-600" />
        <span className="text-xs font-black text-gray-700 uppercase tracking-wider">
          Popular Locations
        </span>
      </div>

      {/* Horizontal Scrollable Location Pills */}
      <div className="flex gap-2 overflow-x-auto pb-2 no-scrollbar">
        {popularLocations.map((location, index) => {
          // Check if this location is currently selected (matches search query)
          const isSelected = searchQuery && 
            (searchQuery.toLowerCase() === location.name.toLowerCase() ||
             searchQuery.toLowerCase().includes(location.name.toLowerCase()));
          
          return (
            <motion.button
              key={index}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: index * 0.05 }}
              whileHover={{ scale: 1.05, y: -2 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => onLocationClick(location.name)}
              className={`flex-shrink-0 group relative rounded-2xl px-4 py-3 flex items-center gap-2 transition-all shadow-sm hover:shadow-md border-2 ${
                isSelected 
                  ? 'bg-gradient-to-br from-green-500 to-green-600 border-green-600 shadow-lg shadow-green-200' 
                  : 'bg-white border-gray-200 hover:border-green-400'
              }`}
            >
              <span className={`text-xl ${isSelected ? 'animate-bounce' : ''}`}>{location.emoji}</span>
              <div className="text-left">
                <div className={`text-sm font-bold transition-colors whitespace-nowrap ${
                  isSelected ? 'text-white' : 'text-gray-900 group-hover:text-green-700'
                }`}>
                  {location.name}
                </div>
                <div className={`text-xs ${isSelected ? 'text-green-100' : 'text-gray-500'}`}>
                  {location.state}
                </div>
              </div>
              <MapPin className={`w-4 h-4 transition-colors ${
                isSelected ? 'text-white' : 'text-gray-400 group-hover:text-green-600'
              }`} />
              {isSelected && (
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  className="absolute -top-1 -right-1 w-5 h-5 bg-yellow-400 rounded-full flex items-center justify-center shadow-md"
                >
                  <span className="text-xs">✓</span>
                </motion.div>
              )}
            </motion.button>
          );
        })}
      </div>
    </motion.div>
  );
}